import {
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import {
  ImpactAnalysisChangedError,
  exportImpactAnalysis,
  type ImpactAnalysisExportFormat,
  type ImpactStateFact,
} from "../../api/impactAnalysis";
import type { DagSummaryProjection } from "./viewProjections";
import type { DagViewerGraph, DagViewerNode } from "./types";
import {
  ModelClassificationBadge,
  NodeModelClassificationBadge,
  exactModelInstanceForNode,
  exactVisibleGenericModel,
  exactVisibleModelInstances,
} from "./ModelClassificationBadge";
import type { HumanProvenanceResult } from "../../api/humanProvenance";
import { HumanProvenanceTable, HumanRecordInspector } from "./HumanProvenancePanel";
import type {
  ImpactClassification,
  ImpactPresentation,
  ImpactPresentationPath,
  ImpactPresentationSubject,
} from "./impactPresentation";
import { abbreviatedSnapshotToken } from "./temporalState";
import "./dag-analysis-panels.css";

const IMPACT_PAGE_SIZE = 50;
const IMPACT_PATH_PAGE_SIZE = 20;
const IMPACT_GAP_PAGE_SIZE = 50;
const SUMMARY_MEMBER_PAGE_SIZE = 50;
const RECORD_BROWSER_PAGE_SIZE = 50;

type UnknownRecord = Record<string, unknown>;

export interface ImpactResultsPanelProps {
  sourceGraph: DagViewerGraph | null;
  presentation: ImpactPresentation | null;
  anchorId: string;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onSelect?: (nodeId: string | null) => void;
  onActivate?: (nodeId: string) => void;
}

export interface SummaryGroupPanelProps {
  sourceGraph: DagViewerGraph;
  projection: DagSummaryProjection;
  groupId: string;
  onClose?: () => void;
  onSelect?: (nodeId: string | null) => void;
  onFocus?: (nodeId: string) => void;
}

export interface AccessibleRecordBrowserProps {
  graph: DagViewerGraph;
  filterMask?: Uint8Array | null;
  selectedId?: string | null;
  onClose?: () => void;
  onSelect?: (nodeId: string | null) => void;
  onActivate?: (nodeId: string) => void;
  onOpenProof?: () => void;
  humanResult?: HumanProvenanceResult | null;
}

type ImpactFilter = "all" | ImpactClassification;

interface GroupCounts {
  members: number;
  internal: number;
  incoming: number;
  outgoing: number;
}

interface SummaryMember {
  id: string;
  node: DagViewerNode | null;
  sourceIndex: number | null;
}

interface RelationshipCount {
  kind: string;
  count: number;
}

interface SummaryConnection {
  id: string;
  direction: "incoming" | "outgoing";
  groupLabel: string;
  count: number;
  relationships: RelationshipCount[];
}

interface BrowserRecord {
  node: DagViewerNode;
  sourceIndex: number;
  searchableText: string;
}

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as UnknownRecord
    : {};
}

function displayString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function recordPath(node: DagViewerNode): string | null {
  return displayString(node.raw.path);
}

function formatHopCount(distance: number | null): string {
  if (distance == null) return "Hop distance not recorded";
  return `${distance} ${distance === 1 ? "hop" : "hops"}`;
}

function humanize(value: string): string {
  return value.split("_").join(" ");
}

function impactClassificationLabel(value: ImpactClassification): string {
  switch (value) {
    case "potential_only": return "Propagation enabled";
    case "explicitly_invalidated": return "Explicitly invalidated";
    case "stopped": return "Propagation stopped";
    case "indeterminate": return "Indeterminate";
  }
}

function pathForSubject(
  paths: readonly ImpactPresentationPath[],
  subject: ImpactPresentationSubject,
): ImpactPresentationPath[] {
  const allowed = new Set(subject.pathIds);
  return paths.filter((path) => allowed.has(path.pathId));
}

function EvidenceLink({
  label,
  id,
  nodeId,
  onActivate,
}: {
  label: string;
  id: string;
  nodeId: string;
  onActivate?: (nodeId: string) => void;
}) {
  return (
    <button
      type="button"
      className="dag-impact-results__evidence-link"
      aria-label={`${label} ${id}`}
      onClick={() => onActivate?.(nodeId)}
    >
      <span>{label}</span><code>{id}</code>
    </button>
  );
}

function StateEvidence({
  kind,
  state,
  onActivate,
}: {
  kind: "Binding" | "Operation";
  state: ImpactStateFact;
  onActivate?: (nodeId: string) => void;
}) {
  const prefix = kind === "Binding" ? "binding_state_event" : "operation_state_event";
  return (
    <section className="dag-impact-results__state-evidence" aria-label={`${kind} state evidence`}>
      <small>{kind} state · {humanize(state.state)}{state.change_cause ? ` · ${humanize(state.change_cause)}` : ""}{state.reason ? ` · ${state.reason}` : ""}</small>
      {state.event_id ? <EvidenceLink label={`${kind} state event`} id={state.event_id} nodeId={`${prefix}:${state.event_id}`} onActivate={onActivate} /> : null}
      {state.effective_at ? <time dateTime={state.effective_at}>Effective {state.effective_at}</time> : null}
      {state.recorded_at ? <time dateTime={state.recorded_at}>Recorded {state.recorded_at}</time> : null}
      {state.updated_at ? <time dateTime={state.updated_at}>Updated {state.updated_at}</time> : null}
    </section>
  );
}

function ImpactEvidence({
  presentation,
  subject,
  onActivate,
}: {
  presentation: ImpactPresentation;
  subject: ImpactPresentationSubject;
  onActivate?: (nodeId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [pathLimit, setPathLimit] = useState(IMPACT_PATH_PAGE_SIZE);
  const subjectPaths = pathForSubject(presentation.paths, subject);
  const visiblePaths = subjectPaths.slice(0, pathLimit);
  return (
    <details className="dag-impact-results__evidence">
      <summary onClick={() => setOpen((current) => {
        if (current) setPathLimit(IMPACT_PATH_PAGE_SIZE);
        return !current;
      })}>Why this conclusion</summary>
      {open ? (
        <div className="dag-impact-results__evidence-body">
          {subjectPaths.length ? (
            <ol className="dag-impact-results__paths">
              {visiblePaths.map((path) => (
                <li key={path.pathId}>
                  <header><strong>{humanize(path.propagationStatus)}</strong><code>{path.pathId}</code></header>
                  {path.causes.length ? <p>Recorded causes: {path.causes.map(humanize).join(", ")}</p> : null}
                  <ol className="dag-impact-results__hops">
                    {path.hops.map((hop) => (
                      <li key={`${path.pathId}:${hop.ordinal}`}>
                        <EvidenceLink label="Input ArtifactVersion" id={hop.inputArtifactVersionNodeId.slice("artifact_version:".length)} nodeId={hop.inputArtifactVersionNodeId} onActivate={onActivate} />
                        <EvidenceLink label="OperationRun" id={hop.operationId} nodeId={`operation_run:${hop.operationId}`} onActivate={onActivate} />
                        <EvidenceLink label={`Input Binding · ${hop.inputRole}[${hop.inputOrdinal}]`} id={hop.inputBindingId} nodeId={`binding:${hop.inputBindingId}`} onActivate={onActivate} />
                        <EvidenceLink label={`Output Binding · ${hop.outputRole}[${hop.outputOrdinal}]`} id={hop.outputBindingId} nodeId={`binding:${hop.outputBindingId}`} onActivate={onActivate} />
                        <EvidenceLink label="Output ArtifactVersion" id={hop.outputArtifactVersionNodeId.slice("artifact_version:".length)} nodeId={hop.outputArtifactVersionNodeId} onActivate={onActivate} />
                        <small>Propagates invalidity · {String(hop.propagatesInvalidity)}</small>
                        <StateEvidence kind="Binding" state={hop.bindingState} onActivate={onActivate} />
                        <StateEvidence kind="Operation" state={hop.operationState} onActivate={onActivate} />
                      </li>
                    ))}
                  </ol>
                  <p>Terminal: {humanize(path.terminalReason)}</p>
                </li>
              ))}
            </ol>
          ) : <p>No visible causal path was returned.</p>}
          {pathLimit < subjectPaths.length ? (
            <button type="button" className="dag-impact-results__load-paths" onClick={() => setPathLimit((current) => current + IMPACT_PATH_PAGE_SIZE)}>
              Show {Math.min(IMPACT_PATH_PAGE_SIZE, subjectPaths.length - pathLimit)} more paths
            </button>
          ) : null}
          <section className="dag-impact-results__receipt" aria-label={`Invalidation attestation for ${subject.label}`}>
            <strong>{subject.receipt.status === "attested" ? "Receipt attested" : subject.receipt.status === "indeterminate" ? "Explicit invalidation evidence is indeterminate" : "No explicit invalidation receipt"}</strong>
            {subject.explicitReason ? <span>Reason · {subject.explicitReason}</span> : null}
            {subject.causedByArtifactVersionId ? <EvidenceLink label="Caused by ArtifactVersion" id={subject.causedByArtifactVersionId} nodeId={`artifact_version:${subject.causedByArtifactVersionId}`} onActivate={onActivate} /> : null}
            {subject.receipt.receiptKind ? <span>Evidence · {humanize(subject.receipt.receiptKind)}</span> : null}
            {subject.explicitEffectiveAt ? <time dateTime={subject.explicitEffectiveAt}>Invalidation effective {subject.explicitEffectiveAt}</time> : null}
            {subject.explicitRecordedAt ? <time dateTime={subject.explicitRecordedAt}>Lifecycle recorded {subject.explicitRecordedAt}</time> : null}
            {subject.receipt.lifecycleEventId ? <EvidenceLink label="Lifecycle event" id={subject.receipt.lifecycleEventId} nodeId={`lifecycle_event:${subject.receipt.lifecycleEventId}`} onActivate={onActivate} /> : null}
            {subject.receipt.auditEventId ? <EvidenceLink label="Audit event" id={subject.receipt.auditEventId} nodeId={`audit_event:${subject.receipt.auditEventId}`} onActivate={onActivate} /> : null}
            {subject.receipt.domainEventId ? <EvidenceLink label="Domain event" id={subject.receipt.domainEventId} nodeId={`domain_event:${subject.receipt.domainEventId}`} onActivate={onActivate} /> : null}
            {subject.receipt.idempotencyKey ? <span>Idempotency · <code>{subject.receipt.idempotencyKey}</code></span> : null}
            {subject.receipt.requestHash ? <span>Request · <code>{subject.receipt.requestHash}</code></span> : null}
            {subject.receipt.recordedAt ? <time dateTime={subject.receipt.recordedAt}>Receipt recorded {subject.receipt.recordedAt}</time> : null}
          </section>
          {subject.exclusionIds.length ? (
            <section className="dag-impact-results__exclusions" aria-label={`Stopped paths for ${subject.label}`}>
              <strong>Propagation exclusions</strong>
              {presentation.exclusions
                .filter((entry) => subject.exclusionIds.includes(entry.exclusionId))
                .map((entry) => <span key={entry.exclusionId}>{humanize(entry.reason)} · hop {entry.hopOrdinal + 1} · <code>{entry.pathId}</code></span>)}
            </section>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}

function ImpactGaps({
  presentation,
  onActivate,
}: {
  presentation: ImpactPresentation;
  onActivate?: (nodeId: string) => void;
}) {
  const gaps = presentation.gaps;
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(IMPACT_GAP_PAGE_SIZE);
  const visible = gaps.slice(0, limit);
  return (
    <details className="dag-impact-results__gaps">
      <summary onClick={() => setOpen((current) => {
        if (current) setLimit(IMPACT_GAP_PAGE_SIZE);
        return !current;
      })}>
        {gaps.length} stated {gaps.length === 1 ? "gap" : "gaps"}
      </summary>
      {open ? (
        <>
          <ul>{visible.map((gap) => {
            const subject = gap.subjectNodeId
              ? presentation.subjects.find((candidate) => candidate.nodeId === gap.subjectNodeId) ?? null
              : null;
            return (
              <li data-testid="dag-impact-gap" key={`${gap.code}:${gap.subjectNodeId ?? ""}`}>
                <span>{humanize(gap.code)}</span>
                {subject ? <EvidenceLink label={`Subject ${subject.label}`} id={subject.artifactVersionId} nodeId={subject.nodeId} onActivate={onActivate} /> : <small>Global analysis gap</small>}
              </li>
            );
          })}</ul>
          {limit < gaps.length ? (
            <button type="button" onClick={() => setLimit((current) => current + IMPACT_GAP_PAGE_SIZE)}>
              Show {Math.min(IMPACT_GAP_PAGE_SIZE, gaps.length - limit)} more gaps
            </button>
          ) : null}
        </>
      ) : null}
    </details>
  );
}

function activateWithKeyboard(
  event: ReactKeyboardEvent<HTMLElement>,
  nodeId: string,
  onActivate?: (nodeId: string) => void,
): void {
  if (event.key !== "Enter" || (!event.ctrlKey && !event.metaKey) || !onActivate) return;
  event.preventDefault();
  event.stopPropagation();
  onActivate(nodeId);
}

function Pager({
  label,
  page,
  pageCount,
  onPage,
}: {
  label: string;
  page: number;
  pageCount: number;
  onPage: (nextPage: number) => void;
}) {
  return (
    <nav className="dag-analysis-panel__pager" aria-label={`${label} pagination`}>
      <button
        type="button"
        disabled={page <= 0}
        onClick={() => onPage(Math.max(0, page - 1))}
        aria-label={`Previous ${label.toLowerCase()} page`}
      >
        ←
      </button>
      <output aria-live="polite">
        Page {page + 1} of {Math.max(1, pageCount)}
      </output>
      <button
        type="button"
        disabled={page >= pageCount - 1}
        onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
        aria-label={`Next ${label.toLowerCase()} page`}
      >
        →
      </button>
    </nav>
  );
}

function ResultsRange({
  page,
  pageSize,
  total,
  noun,
}: {
  page: number;
  pageSize: number;
  total: number;
  noun: string;
}) {
  const first = total ? page * pageSize + 1 : 0;
  const last = Math.min(total, (page + 1) * pageSize);
  return (
    <span className="dag-analysis-panel__range">
      {first}–{last} of {total} {noun}
    </span>
  );
}

function NodeIdentity({ node }: { node: DagViewerNode }) {
  return (
    <span className="dag-analysis-panel__identity">
      <strong>{node.label}</strong>
      <small>{node.id}</small>
    </span>
  );
}

function saveImpactExport(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Cause-aware invalidation results. Graph reachability is context only. */
export function ImpactResultsPanel({
  sourceGraph,
  presentation,
  anchorId,
  isLoading = false,
  error = null,
  onRetry,
  onSelect,
  onActivate,
}: ImpactResultsPanelProps) {
  const headingId = useId();
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<ImpactFilter>("all");
  const [exporting, setExporting] = useState<ImpactAnalysisExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const anchorIndex = sourceGraph?.nodeIndexById.get(anchorId);
  const anchor = anchorIndex == null ? null : sourceGraph?.nodes[anchorIndex] ?? null;
  const records = useMemo(() => {
    return presentation?.subjects.filter((subject) => filter === "all" || subject.classification === filter) ?? [];
  }, [filter, presentation]);

  useEffect(() => {
    setPage(0);
    setFilter("all");
    setExportError(null);
  }, [anchorId, presentation?.effectiveAsOf]);

  const download = async (format: ImpactAnalysisExportFormat) => {
    if (!presentation || exporting) return;
    setExportError(null);
    setExporting(format);
    try {
      const result = await exportImpactAnalysis({
        project_id: presentation.projectId,
        root_ref: { entity_kind: "artifact_version", id: presentation.rootArtifactVersionId },
        as_of: presentation.effectiveAsOf,
        recorded_as_of: presentation.recordedAsOf,
        snapshot_token: presentation.snapshotToken,
        ...presentation.limits,
        expected_semantic_digest: presentation.semanticDigest,
      }, format);
      saveImpactExport(result.blob, result.filename);
    } catch (exportFailure) {
      setExportError(exportFailure instanceof ImpactAnalysisChangedError
        ? "Impact analysis changed. Refresh it before downloading a new export."
        : exportFailure instanceof Error
          ? exportFailure.message
          : "Could not export this impact analysis.");
    } finally {
      setExporting(null);
    }
  };

  const pageCount = Math.max(1, Math.ceil(records.length / IMPACT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visibleRecords = records.slice(
    safePage * IMPACT_PAGE_SIZE,
    (safePage + 1) * IMPACT_PAGE_SIZE,
  );

  return (
    <section className="dag-analysis-panel dag-impact-results" aria-labelledby={headingId}>
      <header className="dag-analysis-panel__header">
        <div>
          <span className="dag-analysis-panel__eyebrow">Typed invalidation analysis</span>
          <h2 id={headingId}>Cause-aware impact</h2>
          <p>
            {presentation
              ? <>
                  Root cause <strong>{presentation.rootLabel}</strong> · lifecycle {humanize(presentation.rootLifecycle.state)}
                  {presentation.rootLifecycle.event_type ? <> via {humanize(presentation.rootLifecycle.event_type)}</> : null}
                  {presentation.rootLifecycle.reason ? <> · {presentation.rootLifecycle.reason}</> : null}
                  {" "}· excluded from every result count.
                </>
              : anchor
                ? <>Downstream conclusions for <strong>{anchor.label}</strong>.</>
                : <>Downstream conclusions for <code>{anchorId}</code>.</>}
            {presentation ? <>
              {" "}Effective/event <time dateTime={presentation.effectiveAsOf}>{presentation.effectiveAsOf}</time>
              {" "}· recorded/system <time dateTime={presentation.recordedAsOf}>{presentation.recordedAsOf}</time>
              {" "}· token <code title={presentation.snapshotToken}>{abbreviatedSnapshotToken(presentation.snapshotToken)}</code>.
            </> : null}
          </p>
        </div>
        <span className="dag-analysis-panel__total" aria-label={`${presentation?.summary.propagationEnabled ?? 0} propagation-enabled records`}>
          {presentation?.summary.propagationEnabled ?? 0}
        </span>
      </header>

      {isLoading ? <div className="dag-analysis-panel__state" role="status">Evaluating typed propagation paths…</div> : null}
      {error ? (
        <div className="dag-analysis-panel__state is-error" role="alert">
          <strong>Impact analysis unavailable</strong>
          <span>{error.message}</span>
          {onRetry ? <button type="button" onClick={onRetry}>Try again</button> : null}
        </div>
      ) : null}

      {presentation ? (
        <>
          <section className="dag-impact-results__root-lifecycle" aria-label="Selected root lifecycle evidence">
            <strong>{humanize(presentation.rootLifecycle.state)}</strong>
            <span>{presentation.rootLifecycle.event_type ? humanize(presentation.rootLifecycle.event_type) : "No lifecycle event recorded"}</span>
            {presentation.rootLifecycle.reason ? <span>{presentation.rootLifecycle.reason}</span> : null}
            {presentation.rootLifecycle.event_id ? <EvidenceLink label="Root lifecycle event" id={presentation.rootLifecycle.event_id} nodeId={`lifecycle_event:${presentation.rootLifecycle.event_id}`} onActivate={onActivate} /> : null}
            {presentation.rootLifecycle.effective_at ? <time dateTime={presentation.rootLifecycle.effective_at}>Effective {presentation.rootLifecycle.effective_at}</time> : null}
            {presentation.rootLifecycle.recorded_at ? <time dateTime={presentation.rootLifecycle.recorded_at}>Recorded {presentation.rootLifecycle.recorded_at}</time> : null}
          </section>
          <dl className="dag-impact-results__summary" aria-label="Impact analysis summary">
            <div><dt>Reachable</dt><dd>{presentation.summary.reachable}</dd></div>
            <div><dt>Propagation enabled</dt><dd>{presentation.summary.propagationEnabled}</dd></div>
            <div><dt>Potential only</dt><dd>{presentation.summary.potentialOnly}</dd></div>
            <div><dt>Explicit</dt><dd>{presentation.summary.explicitlyInvalidated}</dd></div>
            <div><dt>Stopped</dt><dd>{presentation.summary.stopped}</dd></div>
            <div><dt>Indeterminate</dt><dd>{presentation.summary.indeterminate}</dd></div>
          </dl>

          <p className="dag-analysis-panel__notice" role="note">
            Reachability, propagation eligibility, and explicit invalidation are independent recorded facts.
            A reachable record is never labelled affected without a typed propagation conclusion.
          </p>

          {presentation.redacted ? (
            <p className="dag-impact-results__redaction" role="status">
              Restricted evidence was omitted. No hidden identity or count is disclosed.
            </p>
          ) : null}
          {!presentation.complete ? (
            <p className="dag-analysis-panel__boundary" role="status">
              This bounded analysis is incomplete. Review the stated gaps before drawing a negative conclusion.
            </p>
          ) : null}
          {presentation.gaps.length ? (
            <ImpactGaps presentation={presentation} onActivate={onActivate} />
          ) : null}

          <nav className="dag-impact-results__filters" aria-label="Impact result categories">
            {([
              ["all", "All conclusions"],
              ["potential_only", "Potential only"],
              ["explicitly_invalidated", "Explicit"],
              ["stopped", "Stopped"],
              ["indeterminate", "Indeterminate"],
            ] as const).map(([value, label]) => (
              <button type="button" key={value} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
            ))}
          </nav>
        </>
      ) : null}

      <div className="dag-analysis-panel__body">
        {presentation && records.length ? (
          <ol className="dag-analysis-panel__record-list" start={safePage * IMPACT_PAGE_SIZE + 1}>
            {visibleRecords.map((subject) => {
              return (
              <li key={subject.nodeId} data-testid="dag-impact-row" data-impact-classification={subject.classification}>
                <button
                  type="button"
                  className="dag-analysis-panel__record-button"
                  onClick={() => onSelect?.(subject.nodeId)}
                  onDoubleClick={() => onActivate?.(subject.nodeId)}
                  onKeyDown={(event) => activateWithKeyboard(event, subject.nodeId, onActivate)}
                  aria-label={`Select ${impactClassificationLabel(subject.classification)} record ${subject.label}, ${formatHopCount(subject.depth)}`}
                >
                  <span className="dag-analysis-panel__identity">
                    <strong>{subject.label}</strong>
                    <small>{subject.nodeId}</small>
                  </span>
                  <span className={`dag-impact-results__classification is-${subject.classification}`}>
                    {impactClassificationLabel(subject.classification)}
                  </span>
                  <span className="dag-analysis-panel__record-metadata">
                    <span><b>Reachability</b>{humanize(subject.reachabilityStatus)}</span>
                    <span><b>Propagation</b>{humanize(subject.propagationStatus)}</span>
                    <span><b>Explicit</b>{humanize(subject.explicitInvalidationStatus)}</span>
                    <span><b>Lifecycle</b>{humanize(subject.lifecycleState)}</span>
                    <span><b>Causes</b>{subject.causes.length ? subject.causes.map(humanize).join(", ") : "No explicit state cause"}</span>
                    <span><b>Distance</b>{formatHopCount(subject.depth)}</span>
                  </span>
                </button>
                <ImpactEvidence presentation={presentation} subject={subject} onActivate={onActivate} />
              </li>
              );
            })}
          </ol>
        ) : presentation && !records.length ? (
          <div className="dag-analysis-panel__empty">
            <strong>No conclusions in this category</strong>
            <span>Try another category. Reachable-only graph context is intentionally not promoted into this list.</span>
          </div>
        ) : !isLoading && !error ? <div className="dag-analysis-panel__empty"><strong>Waiting for typed analysis</strong></div> : null}
      </div>

      {presentation ? <footer className="dag-analysis-panel__footer">
        <ResultsRange
          page={safePage}
          pageSize={IMPACT_PAGE_SIZE}
          total={records.length}
          noun="records"
        />
        {pageCount > 1 && (
          <Pager label="Impact results" page={safePage} pageCount={pageCount} onPage={setPage} />
        )}
        <div className="dag-analysis-panel__export-digest">
          <span>Displayed semantic digest</span>
          <code title={presentation.semanticDigest}>{presentation.semanticDigest}</code>
        </div>
        {exportError ? (
          <p role="alert">
            {exportError}
            {onRetry ? <button type="button" onClick={() => { setExportError(null); onRetry(); }}>Refresh impact</button> : null}
          </p>
        ) : null}
        <div className="dag-analysis-panel__exports">
          <button type="button" disabled={exporting !== null} onClick={() => void download("json")}>
            {exporting === "json" ? "Preparing JSON…" : "Download JSON"}
          </button>
          <button type="button" disabled={exporting !== null} onClick={() => void download("html")}>
            {exporting === "html" ? "Preparing print view…" : "Printable HTML"}
          </button>
        </div>
      </footer> : null}
    </section>
  );
}

function sourceGroupCounts(graph: DagViewerGraph, memberIds: ReadonlySet<string>): GroupCounts {
  const counts: GroupCounts = {
    members: memberIds.size,
    internal: 0,
    incoming: 0,
    outgoing: 0,
  };
  for (const edge of graph.edges) {
    const sourceId = graph.nodes[edge.source]?.id;
    const targetId = graph.nodes[edge.target]?.id;
    if (!sourceId || !targetId) continue;
    const sourceIsMember = memberIds.has(sourceId);
    const targetIsMember = memberIds.has(targetId);
    if (sourceIsMember && targetIsMember) counts.internal += 1;
    else if (sourceIsMember) counts.outgoing += 1;
    else if (targetIsMember) counts.incoming += 1;
  }
  return counts;
}

function uniqueNeighbourCount(adjacency: number[][], sourceIndex: number): number {
  return new Set(adjacency[sourceIndex] ?? []).size;
}

function uniqueDependencyNeighbourCount(
  graph: DagViewerGraph,
  sourceIndex: number,
  direction: "incoming" | "outgoing",
): number {
  const neighbours = new Set<number>();
  for (const edge of graph.edges) {
    if (edge.kind === "instance_of") continue;
    if (direction === "incoming" && edge.target === sourceIndex) neighbours.add(edge.source);
    if (direction === "outgoing" && edge.source === sourceIndex) neighbours.add(edge.target);
  }
  return neighbours.size;
}

function relationshipCounts(value: unknown, fallbackKind: string, fallbackCount: number): RelationshipCount[] {
  const relationships = Object.entries(asRecord(value))
    .map(([kind, count]) => ({ kind, count: finiteNumber(count) }))
    .filter((entry): entry is RelationshipCount => entry.count != null && entry.count >= 0)
    .sort((a, b) => a.kind.localeCompare(b.kind));
  return relationships.length ? relationships : [{ kind: fallbackKind, count: fallbackCount }];
}

function summaryConnections(projection: DagSummaryProjection, groupId: string): SummaryConnection[] {
  const groupIndex = projection.graph.nodeIndexById.get(groupId);
  if (groupIndex == null) return [];
  const connections: SummaryConnection[] = [];
  for (const edge of projection.graph.edges) {
    let direction: SummaryConnection["direction"] | null = null;
    let otherIndex: number | null = null;
    if (edge.target === groupIndex) {
      direction = "incoming";
      otherIndex = edge.source;
    } else if (edge.source === groupIndex) {
      direction = "outgoing";
      otherIndex = edge.target;
    }
    if (!direction || otherIndex == null) continue;
    const otherGroup = projection.graph.nodes[otherIndex];
    if (!otherGroup) continue;
    const metadata = asRecord(edge.raw.metadata);
    const count = finiteNumber(metadata.relationship_count) ?? 1;
    connections.push({
      id: edge.id,
      direction,
      groupLabel: otherGroup.label,
      count,
      relationships: relationshipCounts(metadata.relationship_counts, edge.kind, count),
    });
  }
  connections.sort((a, b) => (
    a.direction.localeCompare(b.direction)
    || a.groupLabel.localeCompare(b.groupLabel, undefined, { numeric: true, sensitivity: "base" })
    || a.id.localeCompare(b.id)
  ));
  return connections;
}

/** Reveal the source records and source-edge counts hidden by a summary node. */
export function SummaryGroupPanel({
  sourceGraph,
  projection,
  groupId,
  onClose,
  onSelect,
  onFocus,
}: SummaryGroupPanelProps) {
  const headingId = useId();
  const [page, setPage] = useState(0);
  const projectedIndex = projection.graph.nodeIndexById.get(groupId);
  const group = projectedIndex == null ? null : projection.graph.nodes[projectedIndex] ?? null;
  const memberIds = projection.memberIdsByGroup.get(groupId) ?? [];
  const memberIdSet = useMemo(() => new Set(memberIds), [memberIds]);
  const counts = useMemo(() => sourceGroupCounts(sourceGraph, memberIdSet), [memberIdSet, sourceGraph]);
  const members = useMemo<SummaryMember[]>(() => memberIds
    .map((id) => {
      const sourceIndex = sourceGraph.nodeIndexById.get(id);
      return {
        id,
        sourceIndex: sourceIndex ?? null,
        node: sourceIndex == null ? null : sourceGraph.nodes[sourceIndex] ?? null,
      };
    })
    .sort((a, b) => (
      (a.node?.label ?? a.id).localeCompare(b.node?.label ?? b.id, undefined, {
        numeric: true,
        sensitivity: "base",
      }) || a.id.localeCompare(b.id)
    )), [memberIds, sourceGraph]);
  const connections = useMemo(() => summaryConnections(projection, groupId), [groupId, projection]);
  const unavailableCount = members.reduce((total, member) => total + Number(!member.node), 0);
  const reportedMemberCount = finiteNumber(group?.metadata.member_count);

  useEffect(() => setPage(0), [groupId, projection, sourceGraph]);

  const pageCount = Math.max(1, Math.ceil(members.length / SUMMARY_MEMBER_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visibleMembers = members.slice(
    safePage * SUMMARY_MEMBER_PAGE_SIZE,
    (safePage + 1) * SUMMARY_MEMBER_PAGE_SIZE,
  );

  if (!group) {
    return (
      <section className="dag-analysis-panel dag-summary-group" aria-labelledby={headingId}>
        <header className="dag-analysis-panel__header">
          <div>
            <span className="dag-analysis-panel__eyebrow">Summary group</span>
            <h2 id={headingId}>Group unavailable</h2>
            <p>No summary group with ID <code>{groupId}</code> exists in this projection.</p>
          </div>
          {onClose && (
            <button type="button" className="dag-analysis-panel__close" onClick={onClose} aria-label="Close summary group">
              ×
            </button>
          )}
        </header>
      </section>
    );
  }

  return (
    <section className="dag-analysis-panel dag-summary-group" aria-labelledby={headingId}>
      <header className="dag-analysis-panel__header">
        <div>
          <span className="dag-analysis-panel__eyebrow">Summary group</span>
          <h2 id={headingId}>{group.label}</h2>
          <p>
            Grouped by {displayString(group.metadata.group_by) ?? "an unrecorded field"}
            {group.metadata.group_key == null ? "; this group represents missing values." : "."}
          </p>
        </div>
        {onClose && (
          <button type="button" className="dag-analysis-panel__close" onClick={onClose} aria-label="Close summary group">
            ×
          </button>
        )}
      </header>

      <dl className="dag-analysis-panel__metrics" aria-label="Source graph counts for this group">
        <div><dt>Members</dt><dd>{counts.members}</dd></div>
        <div><dt>Internal edges</dt><dd>{counts.internal}</dd></div>
        <div><dt>Incoming edges</dt><dd>{counts.incoming}</dd></div>
        <div><dt>Outgoing edges</dt><dd>{counts.outgoing}</dd></div>
      </dl>

      {(reportedMemberCount != null && reportedMemberCount !== counts.members) && (
        <p className="dag-analysis-panel__warning" role="status">
          The summary record reports {reportedMemberCount} members, while its membership map contains {counts.members}.
          The member list below follows the membership map.
        </p>
      )}
      {unavailableCount > 0 && (
        <p className="dag-analysis-panel__warning" role="status">
          {unavailableCount} {unavailableCount === 1 ? "member is" : "members are"} named by the projection but absent
          from the current source graph.
        </p>
      )}

      <details className="dag-analysis-panel__definitions">
        <summary>How these counts and edges are defined</summary>
        <p>
          Members are the record IDs assigned to this virtual group. Internal edges join two members and are collapsed.
          Incoming and outgoing edges cross the group boundary. Counts come from the current source graph; a line between
          two summary groups represents one or more of those source edges.
        </p>
      </details>

      <section className="dag-analysis-panel__subsection" aria-labelledby={`${headingId}-connections`}>
        <h3 id={`${headingId}-connections`}>Group connections</h3>
        {connections.length ? (
          <ul className="dag-analysis-panel__connection-list">
            {connections.map((connection) => (
              <li key={connection.id}>
                <span className={`dag-analysis-panel__direction is-${connection.direction}`} aria-hidden="true">
                  {connection.direction === "incoming" ? "←" : "→"}
                </span>
                <span>
                  <strong>
                    {connection.count} source {connection.count === 1 ? "edge" : "edges"}
                    {connection.direction === "incoming" ? " from " : " to "}
                    {connection.groupLabel}
                  </strong>
                  <small>
                    {connection.relationships.map(({ kind, count }) => `${kind} × ${count}`).join(" · ")}
                  </small>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dag-analysis-panel__muted">No cross-group connections are present in this summary projection.</p>
        )}
      </section>

      <section className="dag-analysis-panel__subsection dag-analysis-panel__members" aria-labelledby={`${headingId}-members`}>
        <div className="dag-analysis-panel__subsection-heading">
          <h3 id={`${headingId}-members`}>Members</h3>
          <ResultsRange
            page={safePage}
            pageSize={SUMMARY_MEMBER_PAGE_SIZE}
            total={members.length}
            noun="members"
          />
        </div>
        {members.length ? (
          <ul className="dag-analysis-panel__member-list">
            {visibleMembers.map((member) => (
              <li key={member.id} data-testid="dag-summary-member">
                {member.node && member.sourceIndex != null ? (
                  <>
                    <button
                      type="button"
                      className="dag-analysis-panel__member-select"
                      onClick={() => onSelect?.(member.id)}
                      aria-label={`Select group member ${member.node.label}`}
                    >
                      <NodeIdentity node={member.node} />
                      <span className="dag-analysis-panel__member-counts">
                        <span>{uniqueNeighbourCount(sourceGraph.incoming, member.sourceIndex)} parents</span>
                        <span>{uniqueNeighbourCount(sourceGraph.outgoing, member.sourceIndex)} children</span>
                      </span>
                    </button>
                    {onFocus && (
                      <button
                        type="button"
                        className="dag-analysis-panel__focus-action"
                        onClick={() => onFocus(member.id)}
                        aria-label={`Focus lineage on ${member.node.label}`}
                      >
                        Focus
                      </button>
                    )}
                  </>
                ) : (
                  <span className="dag-analysis-panel__missing-member">
                    <strong>{member.id}</strong>
                    <small>Not present in the current source graph</small>
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="dag-analysis-panel__empty">
            <strong>No members are recorded for this group</strong>
            <span>The summary node exists, but its membership map is empty.</span>
          </div>
        )}
        {pageCount > 1 && (
          <Pager label="Summary members" page={safePage} pageCount={pageCount} onPage={setPage} />
        )}
      </section>
    </section>
  );
}

function searchableNodeText(node: DagViewerNode): string {
  const metadataScalars = Object.values(node.metadata)
    .filter((value) => ["string", "number", "boolean"].includes(typeof value))
    .map(String);
  return [
    node.id,
    node.label,
    node.kind,
    node.type,
    node.state,
    node.channel,
    node.digest,
    recordPath(node),
    ...metadataScalars,
  ]
    .filter((value): value is string => typeof value === "string" && Boolean(value))
    .join("\n")
    .toLocaleLowerCase();
}

function AccessibleModelRelation({
  graph,
  node,
  onSelect,
}: {
  graph: DagViewerGraph;
  node: DagViewerNode;
  onSelect?: (nodeId: string | null) => void;
}) {
  const relation = exactModelInstanceForNode(node);
  if (!relation && (node.semanticRole !== "artifact" || node.metadata.boundary === true)) return null;
  const genericTarget = exactVisibleGenericModel(graph, node);
  const visibleInstances = exactVisibleModelInstances(graph, node);
  return (
    <section className="dag-analysis-panel__model-relation" aria-label="Accessible model and instance traversal">
      <header>
        <strong>Model / instance</strong>
        <small>Context only · does not propagate invalidity</small>
      </header>
      {relation?.classification === "instance" ? (
        genericTarget ? (
          <button
            type="button"
            onClick={() => onSelect?.(genericTarget.id)}
            aria-label={`Go to generic model ${genericTarget.label}`}
          >
            <ModelClassificationBadge classification="generic" />
            <span><strong>{genericTarget.label}</strong><small>Exact visible generic Artifact</small></span>
          </button>
        ) : (
          <p role="status">Generic model unavailable in this bounded projection. No target was synthesized and traversal is disabled.</p>
        )
      ) : relation?.classification === "generic" ? (
        visibleInstances.length ? (
          <div>
            {visibleInstances.map((instance) => (
              <button
                type="button"
                key={instance.id}
                onClick={() => onSelect?.(instance.id)}
                aria-label={`Go to model instance ${instance.label}`}
              >
                <ModelClassificationBadge classification="instance" />
                <span><strong>{instance.label}</strong><small>Exact visible instance Artifact</small></span>
              </button>
            ))}
          </div>
        ) : <p role="status">No model instances are visible in this bounded projection.</p>
      ) : (
        <p role="note">No visible generic or instance assertion is available at this exact cut. Nothing was inferred from Artifact kind, name, or metadata.</p>
      )}
    </section>
  );
}

/** A keyboard- and screen-reader-friendly alternative to the canvas. */
export function AccessibleRecordBrowser({
  graph,
  filterMask = null,
  selectedId = null,
  onClose,
  onSelect,
  onActivate,
  onOpenProof,
  humanResult = null,
}: AccessibleRecordBrowserProps) {
  const headingId = useId();
  const searchId = useId();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const maskIsValid = filterMask == null || filterMask.length === graph.nodes.length;
  const activeMask = maskIsValid ? filterMask : null;
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const normalizedTerms = useMemo(() => normalizedQuery.split(/\s+/).filter(Boolean), [normalizedQuery]);

  const candidates = useMemo<BrowserRecord[]>(() => graph.nodes
    .map((node, sourceIndex) => ({ node, sourceIndex, searchableText: searchableNodeText(node) }))
    .filter(({ sourceIndex }) => !activeMask || Boolean(activeMask[sourceIndex])), [activeMask, graph]);
  const results = useMemo(() => {
    const filtered = normalizedTerms.length
      ? candidates.filter(({ searchableText }) => normalizedTerms.every((term) => searchableText.includes(term)))
      : candidates;
    return [...filtered].sort((a, b) => (
      a.node.label.localeCompare(b.node.label, undefined, { numeric: true, sensitivity: "base" })
      || a.node.id.localeCompare(b.node.id)
    ));
  }, [candidates, normalizedTerms]);

  useEffect(() => setPage(0), [graph, filterMask, query]);

  const pageCount = Math.max(1, Math.ceil(results.length / RECORD_BROWSER_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visibleRecords = results.slice(
    safePage * RECORD_BROWSER_PAGE_SIZE,
    (safePage + 1) * RECORD_BROWSER_PAGE_SIZE,
  );
  const selectedIndex = selectedId == null ? null : graph.nodeIndexById.get(selectedId) ?? null;
  const selectedOutsideMask = selectedIndex != null && Boolean(activeMask) && !activeMask?.[selectedIndex];
  const selectedOutsideSearch = selectedIndex != null
    && !selectedOutsideMask
    && Boolean(query.trim())
    && !results.some(({ node }) => node.id === selectedId);
  const selectedNode = selectedIndex == null ? null : graph.nodes[selectedIndex] ?? null;

  return (
    <section className="dag-analysis-panel dag-record-browser" aria-labelledby={headingId}>
      <header className="dag-analysis-panel__header">
        <div>
          <span className="dag-analysis-panel__eyebrow">Accessible graph view</span>
          <h2 id={headingId}>Browse lineage records</h2>
          <p>Search and inspect the same records without using the canvas.</p>
        </div>
        {onClose && (
          <button type="button" className="dag-analysis-panel__close" onClick={onClose} aria-label="Close record browser">
            ×
          </button>
        )}
      </header>

      <div className="dag-analysis-panel__search">
        <label htmlFor={searchId}>Search records</label>
        <input
          id={searchId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            if (query) {
              event.stopPropagation();
              setQuery("");
            } else {
              onClose?.();
            }
          }}
          placeholder="Name, ID, path, type, state…"
          autoComplete="off"
        />
        <output aria-live="polite">
          {results.length} {results.length === 1 ? "record" : "records"}
          {activeMask ? ` in the active ${candidates.length}-record filter` : ""}
        </output>
      </div>

      {onOpenProof ? (
        <button className="dag-viewer__proof-record" data-testid="dag-viewer-proof-trace" type="button" onClick={onOpenProof}>
          Open proof trace for selected ArtifactVersion
        </button>
      ) : null}

      {!maskIsValid && (
        <p className="dag-analysis-panel__warning" role="status">
          The filter mask has {filterMask?.length ?? 0} entries for a {graph.nodes.length}-record graph.
          The mismatched mask was not applied.
        </p>
      )}
      {selectedOutsideMask && (
        <p className="dag-analysis-panel__boundary" role="status">
          The selected record is outside the active graph filter and is not shown in this list.
        </p>
      )}
      {selectedOutsideSearch && (
        <p className="dag-analysis-panel__boundary" role="status">
          The selected record does not match the current search.
        </p>
      )}

      <div className="dag-analysis-panel__table-scroller" tabIndex={0}>
        <table>
          <caption className="dag-analysis-panel__visually-hidden">
            Lineage records with semantic role, model classification, state, dependency parent count, and dependency child count
          </caption>
          <thead>
            <tr>
              <th scope="col">Record</th>
              <th scope="col">Semantic role</th>
              <th scope="col">Model</th>
              <th scope="col">State</th>
              <th scope="col">Dependency parents</th>
              <th scope="col">Dependency children</th>
            </tr>
          </thead>
          <tbody>
            {visibleRecords.map(({ node, sourceIndex }) => {
              const isSelected = node.id === selectedId;
              const path = recordPath(node);
              return (
                <tr key={node.id} className={isSelected ? "is-selected" : undefined} data-testid="dag-record-browser-row">
                  <th scope="row">
                    {node.metadata.interactive === false ? (
                      <span className="dag-analysis-panel__browser-record is-boundary" aria-label={`${node.label}; traversal unavailable`}>
                        <NodeIdentity node={node} />
                        <small>{path ?? "Path not recorded"} · traversal unavailable</small>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="dag-analysis-panel__browser-record"
                        onClick={() => onSelect?.(node.id)}
                        onDoubleClick={() => onActivate?.(node.id)}
                        onKeyDown={(event) => activateWithKeyboard(event, node.id, onActivate)}
                        aria-pressed={isSelected}
                        aria-label={`Select lineage record ${node.label}`}
                      >
                        <NodeIdentity node={node} />
                        <small>{path ?? "Path not recorded"}</small>
                      </button>
                    )}
                  </th>
                  <td><span className="dag-analysis-panel__type-cell">{humanize(node.semanticRole)}<small>{node.kind}</small></span></td>
                  <td><NodeModelClassificationBadge node={node} /></td>
                  <td>{node.state ?? "Not recorded"}</td>
                  <td>{uniqueDependencyNeighbourCount(graph, sourceIndex, "incoming")}</td>
                  <td>{uniqueDependencyNeighbourCount(graph, sourceIndex, "outgoing")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!results.length && (
          <div className="dag-analysis-panel__empty">
            <strong>No records match</strong>
            <span>{query.trim() ? "Try a different search." : "The active graph filter contains no records."}</span>
          </div>
        )}
      </div>

      {humanResult ? (
        <HumanProvenanceTable result={humanResult} selectedId={selectedId} onSelect={(id) => onSelect?.(id)} />
      ) : null}
      {selectedNode ? <AccessibleModelRelation graph={graph} node={selectedNode} onSelect={onSelect} /> : null}
      {selectedNode ? (
        <HumanRecordInspector
          node={selectedNode}
          result={humanResult}
          onSelect={(id) => onSelect?.(id)}
          availableNodeIds={graph.nodeIndexById}
        />
      ) : null}

      <footer className="dag-analysis-panel__footer">
        <ResultsRange
          page={safePage}
          pageSize={RECORD_BROWSER_PAGE_SIZE}
          total={results.length}
          noun="records"
        />
        {pageCount > 1 && (
          <Pager label="Record browser" page={safePage} pageCount={pageCount} onPage={setPage} />
        )}
      </footer>
    </section>
  );
}
